import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { crmHeaders } from "../utils/crmApi";
import { useFloatingWidget } from "../hooks/useFloatingWidget";
import "../styles/ai-chat-widget.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const STORAGE_KEY = "crm_ai_chat_history";

const QUICK_PROMPTS = [
    "Kitne clients/leads hain mere access mein?",
    "CRM mein kaun se email accounts hain?",
    "Find client by email or business name",
    "Write a professional follow-up email",
];

const WELCOME_MESSAGE = {
    role: "assistant",
    content:
        "Hi! Main aapka CRM AI assistant hoon. Mujh se pooch sakte hain:\n• Kisi user, client, ya email account ki details\n• Kitne leads/clients hain\n• Follow-ups aur countries\n• Email likhne ya reply ki madad\n\nExample: \"Ahmed user ka email kya hai?\" ya \"USA ke clients dikhao\"",
};

function loadHistory() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [WELCOME_MESSAGE];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length ? parsed : [WELCOME_MESSAGE];
    } catch {
        return [WELCOME_MESSAGE];
    }
}

export default function AiChatWidget() {
    const [open, setOpen] = useState(false);
    const { rootStyle, dragHandleProps, minimized, minimize, restore } = useFloatingWidget("ai-help");
    const [messages, setMessages] = useState(loadHistory);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const bodyRef = useRef(null);
    const inputRef = useRef(null);
    const username = Cookies.get("username") || "User";

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
        } catch {
            /* ignore */
        }
    }, [messages]);

    useEffect(() => {
        if (open && bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, open, loading]);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [open]);

    const sendMessage = async (text) => {
        const trimmed = (text || input).trim();
        if (!trimmed || loading) return;

        setError("");
        setInput("");

        const userMsg = { role: "user", content: trimmed };
        const nextMessages = [...messages, userMsg];
        setMessages(nextMessages);
        setLoading(true);

        try {
            const history = nextMessages
                .filter((m) => m.role === "user" || m.role === "assistant")
                .slice(-14)
                .map((m) => ({ role: m.role, content: m.content }));

            const res = await axios.post(
                `${BASE_URL}/ai/chat`,
                { message: trimmed, history: history.slice(0, -1) },
                { headers: crmHeaders() }
            );

            setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply }]);
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                "Could not reach AI assistant. Check server connection.";
            setError(msg);
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: `Sorry, I couldn't respond: ${msg}`,
                    isError: true,
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const clearChat = () => {
        setMessages([WELCOME_MESSAGE]);
        setError("");
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            /* ignore */
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div
            className={`ai-chat-widget floating-widget-root ${open ? "ai-chat-widget--open" : ""} ${minimized ? "ai-chat-widget--minimized" : ""}`}
            style={rootStyle}
        >
            {open && !minimized && (
                <div className="ai-chat-panel" role="dialog" aria-label="AI Assistant">
                    <div className="ai-chat-panel-header">
                        <span className="floating-widget-grip" {...dragHandleProps} aria-hidden="true">
                            <i className="bi bi-grip-vertical"></i>
                        </span>
                        <div className="ai-chat-panel-header-main">
                            <div className="ai-chat-avatar">
                                <i className="bi bi-stars"></i>
                            </div>
                            <div>
                                <strong>CRM AI Assistant</strong>
                                <span>Hi {username} · live CRM data</span>
                            </div>
                        </div>
                        <div className="ai-chat-panel-header-actions">
                            <button
                                type="button"
                                className="ai-chat-icon-btn"
                                onClick={clearChat}
                                title="Clear chat"
                            >
                                <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                            <button
                                type="button"
                                className="ai-chat-icon-btn"
                                onClick={() => setOpen(false)}
                                title="Minimize"
                            >
                                <i className="bi bi-dash-lg"></i>
                            </button>
                            <button
                                type="button"
                                className="ai-chat-icon-btn"
                                onClick={() => {
                                    setOpen(false);
                                    minimize();
                                }}
                                title="Minimize to icon"
                            >
                                <i className="bi bi-arrows-angle-contract"></i>
                            </button>
                        </div>
                    </div>

                    <div className="ai-chat-panel-body" ref={bodyRef}>
                        {messages.map((msg, index) => (
                            <div
                                key={`${msg.role}-${index}`}
                                className={`ai-chat-bubble ai-chat-bubble--${msg.role} ${
                                    msg.isError ? "ai-chat-bubble--error" : ""
                                }`}
                            >
                                {msg.role === "assistant" && (
                                    <div className="ai-chat-bubble-icon">
                                        <i className="bi bi-robot"></i>
                                    </div>
                                )}
                                <div className="ai-chat-bubble-text">{msg.content}</div>
                            </div>
                        ))}

                        {loading && (
                            <div className="ai-chat-bubble ai-chat-bubble--assistant ai-chat-bubble--typing">
                                <div className="ai-chat-bubble-icon">
                                    <i className="bi bi-robot"></i>
                                </div>
                                <div className="ai-chat-typing">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="ai-chat-quick-prompts">
                        {QUICK_PROMPTS.map((prompt) => (
                            <button
                                key={prompt}
                                type="button"
                                className="ai-chat-quick-btn"
                                onClick={() => sendMessage(prompt)}
                                disabled={loading}
                            >
                                {prompt}
                            </button>
                        ))}
                    </div>

                    {error && <div className="ai-chat-inline-error">{error}</div>}

                    <div className="ai-chat-panel-footer">
                        <textarea
                            ref={inputRef}
                            className="ai-chat-input"
                            rows={1}
                            placeholder="Poochho — user, client, email account, leads..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={loading}
                        />
                        <button
                            type="button"
                            className="ai-chat-send"
                            onClick={() => sendMessage()}
                            disabled={loading || !input.trim()}
                            aria-label="Send message"
                        >
                            <i className="bi bi-send-fill"></i>
                        </button>
                    </div>
                </div>
            )}

            <span className="floating-widget-grip floating-widget-grip--launcher" {...dragHandleProps} aria-hidden="true">
                <i className="bi bi-grip-vertical"></i>
            </span>

            <button
                type="button"
                className={`ai-chat-launcher ${minimized ? "ai-chat-launcher--mini" : ""}`}
                onClick={() => {
                    if (minimized) restore();
                    setOpen((v) => !v);
                }}
                aria-label={open ? "Close AI assistant" : "Open AI assistant"}
            >
                {open && !minimized ? (
                    <i className="bi bi-chevron-down"></i>
                ) : (
                    <>
                        <i className="bi bi-stars"></i>
                        {!minimized && <span className="ai-chat-launcher-label">AI Help</span>}
                    </>
                )}
            </button>
        </div>
    );
}
